import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useLicencia } from '../context/LicenciaContext';
import { useAuth } from '../context/AuthContext';
import { MONEDAS, useFormatMoneda } from '../utils/moneda';
import { CODIGOS_AREA } from '../utils/codigosArea';

const fmt = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n || 0);
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function Configuracion() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [cambiandoMoneda, setCambiandoMoneda] = useState(false);
  const [horario, setHorario] = useState({ hora_cierre_cartera: 22, hora_apertura_cartera: 5 });
  const [guardandoHorario, setGuardandoHorario] = useState(false);
  const [whatsapp, setWhatsapp] = useState({ codigo_area: '', numero_empresa: '' });
  const [guardandoWhatsapp, setGuardandoWhatsapp] = useState(false);
  const fileRef = useRef(null);
  const { moneda, verificar } = useLicencia();
  const formatMoneda = useFormatMoneda();
  const { usuario } = useAuth();

  const cargar = () => {
    api.get('/config/plan').then(r => {
      setData(r.data);
      setHorario({
        hora_cierre_cartera: r.data.hora_cierre_cartera ?? 22,
        hora_apertura_cartera: r.data.hora_apertura_cartera ?? 5,
      });
      setWhatsapp({
        codigo_area: r.data.codigo_area || '',
        numero_empresa: r.data.numero_empresa || '',
      });
    }).finally(() => setLoading(false));
  };

  useEffect(cargar, []);

  const guardarHorario = async () => {
    setGuardandoHorario(true);
    try {
      await api.put('/config/horario-cartera', horario);
      toast.success('Horario de cartera actualizado');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al guardar el horario');
    } finally {
      setGuardandoHorario(false);
    }
  };

  const guardarWhatsapp = async () => {
    setGuardandoWhatsapp(true);
    try {
      await api.put('/config/whatsapp', whatsapp);
      toast.success('Configuración de WhatsApp actualizada');
      verificar(); // refrescar código de área/número usados en los recordatorios
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al guardar la configuración de WhatsApp');
    } finally {
      setGuardandoWhatsapp(false);
    }
  };

  const subirLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('logo', file);
    setUploadingLogo(true);
    try {
      await api.post('/config/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Logo actualizado');
      cargar();
      verificar(); // refrescar en el sidebar
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al subir logo');
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const eliminarLogo = async () => {
    if (!window.confirm('¿Eliminar el logo actual?')) return;
    try {
      await api.delete('/config/logo');
      toast.success('Logo eliminado');
      cargar();
      verificar();
    } catch { toast.error('Error al eliminar logo'); }
  };

  const cambiarMoneda = async (nuevaMoneda) => {
    setCambiandoMoneda(true);
    try {
      await api.put('/config/moneda', { moneda: nuevaMoneda });
      toast.success('Moneda actualizada');
      verificar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al cambiar moneda');
    } finally {
      setCambiandoMoneda(false);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', color: 'var(--text-3)', padding: 60 }}>Cargando...</div>;

  const s = data?.suscripcion;
  const pct = data?.porcentaje_uso || 0;
  const barColor = pct >= 90 ? '#f87171' : pct >= 70 ? '#fbbf24' : '#34d399';
  const logoActual = data?.logo_url ? `${API_BASE}${data.logo_url}` : null;

  return (
    <div style={{ maxWidth: 740, margin: '0 auto' }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text-1)' }}>Configuración</h1>
        <p style={{ margin: '4px 0 0', color: 'var(--text-3)', fontSize: 14 }}>Estado de tu licencia Prestam.io</p>
      </div>

      {/* ── Logo de la empresa ─────────────────────────────────────── */}
      <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 24, border: '1px solid var(--border)', marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 16px', color: 'var(--text-2)', fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Logo de la empresa</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          {/* Preview */}
          <div style={{ width: 120, height: 72, borderRadius: 10, border: '2px dashed #334155', background: 'var(--bg-app)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
            {logoActual
              ? <img src={logoActual} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              : <span style={{ color: '#334155', fontSize: 28 }}>🏢</span>
            }
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ margin: '0 0 12px', color: 'var(--text-3)', fontSize: 13, lineHeight: 1.6 }}>
              El logo se mostrará en la barra lateral de todos los usuarios de tu empresa.<br />
              Formatos: JPG, PNG, WEBP, SVG · Máximo 2 MB.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={subirLogo} />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploadingLogo}
                style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: uploadingLogo ? 0.6 : 1 }}
              >
                {uploadingLogo ? 'Subiendo...' : logoActual ? 'Cambiar logo' : 'Subir logo'}
              </button>
              {logoActual && (
                <button
                  onClick={eliminarLogo}
                  style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#450a0a', color: '#fca5a5', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                >
                  Eliminar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Moneda de la empresa ───────────────────────────────────── */}
      <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 24, marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 16px', color: 'var(--text-2)', fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Moneda de la empresa</h3>
        <p style={{ color: 'var(--text-3)', fontSize: 13, margin: '0 0 16px' }}>Define la moneda con la que se mostrarán todos los montos en el sistema.</p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={moneda}
            onChange={(e) => cambiarMoneda(e.target.value)}
            disabled={cambiandoMoneda}
            style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-app)', color: 'var(--text-1)', fontSize: 14, minWidth: 260, cursor: 'pointer' }}
          >
            {MONEDAS.map(m => (
              <option key={m.codigo} value={m.codigo}>{m.codigo} — {m.nombre} ({m.pais})</option>
            ))}
          </select>
          <span style={{ color: 'var(--text-3)', fontSize: 13 }}>Ejemplo: {formatMoneda(1250000)}</span>
        </div>
      </div>

      {/* ── Horario de cartera ─────────────────────────────────────── */}
      {usuario?.rol === 'admin' && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 24, border: '1px solid var(--border)', marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 6px', color: 'var(--text-2)', fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Horario de cartera</h3>
          <p style={{ margin: '0 0 16px', color: 'var(--text-3)', fontSize: 13, lineHeight: 1.6 }}>
            Fuera de este horario, los cobradores no podrán registrar cobros ni hacer cambios en la cartera.
          </p>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', color: 'var(--text-3)', fontSize: 12, marginBottom: 5 }}>Hora de cierre</label>
              <select
                value={horario.hora_cierre_cartera}
                onChange={e => setHorario({ ...horario, hora_cierre_cartera: parseInt(e.target.value, 10) })}
                style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-app)', color: 'var(--text-1)', fontSize: 14, outline: 'none' }}
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>{h.toString().padStart(2, '0')}:00</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', color: 'var(--text-3)', fontSize: 12, marginBottom: 5 }}>Hora de apertura</label>
              <select
                value={horario.hora_apertura_cartera}
                onChange={e => setHorario({ ...horario, hora_apertura_cartera: parseInt(e.target.value, 10) })}
                style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-app)', color: 'var(--text-1)', fontSize: 14, outline: 'none' }}
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>{h.toString().padStart(2, '0')}:00</option>
                ))}
              </select>
            </div>
            <button
              onClick={guardarHorario}
              disabled={guardandoHorario}
              style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: guardandoHorario ? 0.6 : 1 }}
            >
              {guardandoHorario ? 'Guardando...' : 'Guardar horario'}
            </button>
          </div>
        </div>
      )}

      {/* ── WhatsApp ───────────────────────────────────────────────── */}
      {usuario?.rol === 'admin' && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 24, border: '1px solid var(--border)', marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 6px', color: 'var(--text-2)', fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>WhatsApp</h3>
          <p style={{ margin: '0 0 16px', color: 'var(--text-3)', fontSize: 13, lineHeight: 1.6 }}>
            Cada cobrador envía los recordatorios desde su propio WhatsApp (no hay una cuenta oficial).
            Aquí puedes fijar el código de área que se usa para armar el número del cliente
            (por defecto se calcula según la moneda de la empresa) y el número de contacto
            de la empresa, que se agrega como firma en los mensajes.
          </p>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', color: 'var(--text-3)', fontSize: 12, marginBottom: 5 }}>Código de área</label>
              <select
                value={whatsapp.codigo_area}
                onChange={e => setWhatsapp({ ...whatsapp, codigo_area: e.target.value })}
                style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-app)', color: 'var(--text-1)', fontSize: 14, outline: 'none', minWidth: 220 }}
              >
                <option value="">Automático (según moneda)</option>
                {CODIGOS_AREA.map(c => (
                  <option key={c.codigo} value={c.codigo}>+{c.codigo} — {c.pais}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', color: 'var(--text-3)', fontSize: 12, marginBottom: 5 }}>Número de la empresa</label>
              <input
                type="text"
                value={whatsapp.numero_empresa}
                onChange={e => setWhatsapp({ ...whatsapp, numero_empresa: e.target.value })}
                placeholder="Ej: 300 123 4567"
                style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-app)', color: 'var(--text-1)', fontSize: 14, outline: 'none', minWidth: 200, boxSizing: 'border-box' }}
              />
            </div>
            <button
              onClick={guardarWhatsapp}
              disabled={guardandoWhatsapp}
              style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: guardandoWhatsapp ? 0.6 : 1 }}
            >
              {guardandoWhatsapp ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      )}

      {data?.configurado && data?.suscripcion ? (
        <>
          {/* Plan card */}
          <div style={{
            background: 'var(--bg-card)', borderRadius: 16, padding: 28,
            border: `2px solid ${s.plan_color || '#6366f1'}40`,
            marginBottom: 20
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <div style={{ width: 12, height: 12, borderRadius: '50%', background: s.plan_color || '#6366f1' }} />
                  <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text-1)' }}>Plan {s.plan_nombre}</h2>
                  <span style={{
                    padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                    background: s.estado === 'activo' ? '#34d39922' : '#fbbf2422',
                    color: s.estado === 'activo' ? '#34d399' : '#fbbf24',
                    textTransform: 'capitalize'
                  }}>{s.estado}</span>
                </div>
                {s.plan_descripcion && <p style={{ margin: 0, color: 'var(--text-3)', fontSize: 13 }}>{s.plan_descripcion}</p>}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: s.plan_color || '#6366f1' }}>{fmt(s.precio_mensual)}<span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400 }}>/mes</span></div>
                {s.precio_anual && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{fmt(s.precio_anual)}/año</div>}
              </div>
            </div>

            {/* Seats gauge */}
            <div style={{ background: 'var(--bg-app)', borderRadius: 12, padding: '20px 24px', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                <span style={{ fontWeight: 700, color: 'var(--text-1)', fontSize: 15 }}>Uso de sillas (usuarios activos)</span>
                <span style={{ fontSize: 13, color: 'var(--text-3)' }}>{data.sillas_usadas} / {s.max_sillas}</span>
              </div>
              <div style={{ background: 'var(--bg-card)', borderRadius: 8, height: 14, overflow: 'hidden', marginBottom: 12 }}>
                <div style={{
                  width: `${Math.min(pct, 100)}%`,
                  background: barColor,
                  height: '100%',
                  borderRadius: 8,
                  transition: 'width 0.5s',
                }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <MetricBox label="Usadas" value={data.sillas_usadas} color={barColor} />
                <MetricBox label="Libres" value={data.sillas_libres} color="#34d399" />
                <MetricBox label="Total" value={s.max_sillas} color="#6366f1" />
              </div>
              {pct >= 90 && (
                <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, background: '#f8717122', border: '1px solid #f8717140', fontSize: 13, color: '#f87171' }}>
                  ⚠ Estás al {pct}% de capacidad. Considera solicitar un upgrade de plan.
                </div>
              )}
              {pct >= 70 && pct < 90 && (
                <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, background: '#fbbf2422', border: '1px solid #fbbf2440', fontSize: 13, color: '#fbbf24' }}>
                  Estás al {pct}% de capacidad. Todavía tienes {data.sillas_libres} silla(s) disponible(s).
                </div>
              )}
            </div>

            {/* Plan features */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
              <FeatureRow icon="💺" label="Sillas máximas" value={s.max_sillas === 200 ? 'Sin límite' : s.max_sillas} />
              <FeatureRow icon="📋" label="Cobros/mes" value={s.max_cobros_mes === 0 ? 'Ilimitados' : s.max_cobros_mes?.toLocaleString()} />
              <FeatureRow icon="🎧" label="Soporte" value={<span style={{ textTransform: 'capitalize' }}>{s.soporte}</span>} />
              <FeatureRow icon="🔄" label="Ciclo de pago" value={<span style={{ textTransform: 'capitalize' }}>{s.ciclo}</span>} />
              {s.fecha_inicio && <FeatureRow icon="📅" label="Inicio" value={s.fecha_inicio} />}
              {s.fecha_fin && (
                <FeatureRow
                  icon="⏳"
                  label="Vence"
                  value={<span style={{ color: (() => { const d = Math.ceil((new Date(s.fecha_fin) - new Date()) / 86400000); return d <= 30 ? '#f87171' : '#e2e8f0'; })() }}>{s.fecha_fin}</span>}
                />
              )}
            </div>
          </div>

          {/* Need upgrade? */}
          <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: '18px 24px', border: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 600, color: 'var(--text-1)', fontSize: 14, marginBottom: 2 }}>¿Necesitas más sillas o funciones?</div>
              <div style={{ color: 'var(--text-3)', fontSize: 13 }}>Contacta a tu administrador de Prestam.io para solicitar un upgrade de plan.</div>
            </div>
            <a
              href="mailto:admin@prestam.io?subject=Solicitud de upgrade de plan"
              style={{ padding: '9px 18px', borderRadius: 8, background: '#6366f1', color: '#fff', textDecoration: 'none', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              Solicitar upgrade
            </a>
          </div>
        </>
      ) : data?.es_superadmin ? (
        /* Superadmin SaaS — no tiene suscripción propia */
        <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 32, border: '1px solid var(--border)', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>⚡</div>
          <h2 style={{ margin: '0 0 8px', color: 'var(--text-1)', fontSize: 18 }}>Panel de Superadmin</h2>
          <p style={{ color: 'var(--text-3)', fontSize: 14, lineHeight: 1.6 }}>
            Como superadmin tienes acceso completo a Prestam.io.<br />
            Gestiona empresas, planes y suscripciones desde el Panel SaaS.
          </p>
        </div>
      ) : (
        /* Sin suscripción activa */
        <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 32, border: '1px solid var(--border)', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>🔒</div>
          <h2 style={{ margin: '0 0 8px', color: 'var(--text-1)', fontSize: 18 }}>Sin suscripción activa</h2>
          <p style={{ color: 'var(--text-3)', fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
            Tu empresa no tiene un plan activo en este momento.<br />
            Contacta al administrador de Prestam.io para activar o renovar tu suscripción.
          </p>
          <a
            href="mailto:soporte@prestam.io?subject=Activación de suscripción"
            style={{ display: 'inline-block', padding: '10px 24px', borderRadius: 8, background: '#6366f1', color: '#fff', textDecoration: 'none', fontWeight: 600, fontSize: 14 }}
          >
            Contactar soporte
          </a>
        </div>
      )}
    </div>
  );
}

function MetricBox({ label, value, color }) {
  return (
    <div style={{ background: 'var(--bg-card)', borderRadius: 10, padding: '14px 16px', textAlign: 'center' }}>
      <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    </div>
  );
}

function FeatureRow({ icon, label, value }) {
  return (
    <div style={{ background: 'var(--bg-app)', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ fontSize: 16 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{label}</div>
        <div style={{ fontSize: 13, color: 'var(--text-1s)', fontWeight: 500 }}>{value}</div>
      </div>
    </div>
  );
}
